import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MomentumTrackContainer.css";

const MomentumTrackContainer = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortAsc, setSortAsc] = useState(true);
  const [setupPerformance, setSetupPerformance] = useState({});

  const [showPopup, setShowPopup] = useState(false);
  const [configData, setConfigData] = useState({
    clientid: "",
    totalCapital: "",
    compounding: "false",
    mtf: "false",
    leverage: "",
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          " https://quatdashboard.com:8084/api/momentumbucket",
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setData(res.data);
        setFiltered(res.data);

        const perfRes = await axios.get(
          " https://quatdashboard.com:8084/api/momentumbucket/setup-performance",
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSetupPerformance(perfRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFiltered(
      data.filter(
        (item) =>
          item.symbol.toLowerCase().includes(term) ||
          item.setup.toLowerCase().includes(term) ||
          item.status.toLowerCase().includes(term),
      ),
    );
  };

  const handleSortByStatus = () => {
    const sorted = [...filtered].sort((a, b) =>
      sortAsc
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status),
    );
    setFiltered(sorted);
    setSortAsc(!sortAsc);
  };

  const handleConfigSave = async () => {
    setSavingConfig(true);
    try {
      await axios.post(
        " https://quatdashboard.com:8084/api/testinvesting/allocate",
        configData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert("✅ Configuration updated successfully!");
      setShowPopup(false);
    } catch (err) {
      console.error("❌ Error updating config:", err);
      alert("Failed to update configuration!");
    } finally {
      setSavingConfig(false);
    }
  };

  // ─── Stats ───────────────────────────────────────────────────────────────
  const totalPnL = data.reduce((sum, i) => sum + (i.pnl || 0), 0);
  const totalActive = data.filter((i) => i.status === "active").length;
  const totalExited = data.filter((i) => i.status === "exited").length;
  const totalAlloc = data
    .filter((i) => i.status === "active")
    .reduce((sum, i) => sum + (i.allocation || 0), 0);

  // ─── CAGR ────────────────────────────────────────────────────────────────
  const firstTradeDate =
    data.length > 0
      ? new Date(Math.min(...data.map((i) => new Date(i.entry_date).getTime())))
      : null;

  const yearsElapsed = firstTradeDate
    ? (new Date() - firstTradeDate) / (1000 * 60 * 60 * 24 * 365.25)
    : 0;

  const baseCapital = 500000;
  const portReturnPct = totalPnL ? (totalPnL / baseCapital) * 100 : 0;

  const portfolioCAGR =
    yearsElapsed > 0
      ? (Math.pow(1 + portReturnPct / 100, 1 / yearsElapsed) - 1) * 100
      : null;

  const niftyTotalPct =
    data.find((t) => t.stnifty_percent != null)?.stnifty_percent ?? null;

  const niftyCAGR =
    niftyTotalPct !== null && yearsElapsed > 0
      ? (Math.pow(1 + niftyTotalPct / 100, 1 / yearsElapsed) - 1) * 100
      : null;

  const alpha =
    portfolioCAGR !== null && niftyCAGR !== null
      ? portfolioCAGR - niftyCAGR
      : null;

  const sinceLabel = firstTradeDate
    ? firstTradeDate.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

  const fmtCAGR = (val) => {
    if (val === null || isNaN(val)) return "—";
    return `${val >= 0 ? "+" : ""}${val.toFixed(2)}%`;
  };

  const niftyChangeVal =
    data.find((t) => t.status === "active" && t.stnifty_percent != null)
      ?.stnifty_percent ?? 0;

  const pnlPercent = totalPnL ? (totalPnL / baseCapital) * 100 : 0;

  return (
    <div className="momentum-dashboard">
      {/* ── Header ── */}
      <header className="dashboard-header">
        <h1>Nifty50 - Momentum Tracker</h1>
        <div className="header-right">
          <input
            type="text"
            placeholder="Search symbol, setup or status..."
            value={searchTerm}
            onChange={handleSearch}
          />
          <button className="config-btn" onClick={() => setShowPopup(true)}>
            ⚙️ Add Clients
          </button>
        </div>
      </header>

      {/* ── Summary Cards — ROW 1 (5 cards) + ROW 2 (5 cards) ── */}
      <div className="summary-cards-container">
        {/* Row 1 */}
        <div className="summary-card pnl">
          <h3>Total PnL</h3>
          <p className={totalPnL >= 0 ? "positive" : "negative"}>
            ₹{totalPnL.toLocaleString()}
          </p>
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card active">
          <h3>Active Trades</h3>
          <p>{totalActive}</p>
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card exited">
          <h3>Exited Trades</h3>
          <p>{totalExited}</p>
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card allocation">
          <h3>Total Allocation</h3>
          <p>₹{totalAlloc.toLocaleString()}</p>
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card nifty-change">
          <h3>Nifty Change</h3>
          <p style={{ color: niftyChangeVal >= 0 ? "#6ee7b7" : "#fca5a5" }}>
            {niftyChangeVal.toFixed(2)}%
          </p>
          <small className="card-since">since {sinceLabel}</small>
        </div>

        {/* Row 2 */}
        <div className="summary-card pnl-percent">
          <h3>PnL %</h3>
          <p style={{ color: pnlPercent >= 0 ? "#6ee7b7" : "#fca5a5" }}>
            {pnlPercent.toFixed(2)}%
          </p>
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card cagr-port">
          <h3>Portfolio CAGR</h3>
          <p
            style={{ color: (portfolioCAGR ?? 0) >= 0 ? "#6ee7b7" : "#fca5a5" }}
          >
            {fmtCAGR(portfolioCAGR)}
          </p>
          <small className="card-since">
            since {sinceLabel}
            {yearsElapsed > 0 && yearsElapsed < 1 ? " (ann.)" : ""}
          </small>
        </div>

        <div className="summary-card cagr-nifty">
          <h3>Nifty 50 CAGR</h3>
          <p style={{ color: (niftyCAGR ?? 0) >= 0 ? "#6ee7b7" : "#fca5a5" }}>
            {fmtCAGR(niftyCAGR)}
          </p>
          <small className="card-since">since {sinceLabel}</small>
        </div>

        <div className="summary-card alpha">
          <h3>QUAT vs Nifty 50</h3>
          <p style={{ color: (alpha ?? 0) >= 0 ? "#6ee7b7" : "#fca5a5" }}>
            {fmtCAGR(alpha)}
          </p>
          {alpha !== null && (
            <span
              className={`beat-badge ${alpha >= 0 ? "beat-yes" : "beat-no"}`}
            >
              {alpha >= 0 ? "✅ Beating benchmark" : "⚠️ Below benchmark"}
            </span>
          )}
          <small className="card-since">&nbsp;</small>
        </div>

        <div className="summary-card setup-performance">
          <h3>Setup Performance</h3>
          <div className="setup-grid">
            {Object.entries(setupPerformance).map(([setup, percent]) => (
              <div key={setup} className="setup-item">
                <span className="setup-label">{setup}:</span>
                <span
                  className="setup-value"
                  style={{ color: percent >= 0 ? "#6ee7b7" : "#fca5a5" }}
                >
                  {percent.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Benchmark Note ── */}
      <div className="benchmark-note">
        <span className="benchmark-icon">📊</span>
        <div>
          <strong>Benchmark: Nifty 50 Index</strong>
          <span>
            We invest exclusively in Nifty 50 stocks and measure our performance
            against the Nifty 50 index. The goal is to consistently generate{" "}
            <strong>QUAT</strong> — returns above the benchmark — through
            disciplined momentum-based stock selection. Beating the index means
            your money is working harder than a passive index fund.
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-container full-screen">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          <table className="momentum-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Setup</th>
                <th>Entry Price</th>
                <th>Entry Date</th>
                <th>LTP</th>
                <th>SL</th>
                <th>Target</th>
                <th>Quantity</th>
                <th>Allocation</th>
                <th>Exit Price</th>
                <th>Exit Date</th>
                <th>Charges</th>
                <th>PnL</th>
                <th onClick={handleSortByStatus} className="clickable-header">
                  Status ⬍
                </th>
                <th>Days Passed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="15" className="no-data">
                    No matching records found.
                  </td>
                </tr>
              ) : (
                filtered.map((item, index) => (
                  <tr key={index}>
                    <td>{item.symbol}</td>
                    <td>{item.setup}</td>
                    <td>{item.entry_price.toFixed(2)}</td>
                    <td>
                      {new Date(item.entry_date).toLocaleDateString("en-GB")}
                    </td>
                    <td>
                      {item.ltp && item.entry_price ? (
                        (() => {
                          const diff = item.ltp - item.entry_price;
                          const percent = (
                            (diff / item.entry_price) *
                            100
                          ).toFixed(2);
                          const isUp = diff > 0;
                          return (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                minWidth: "120px",
                                fontWeight: 600,
                                color: isUp ? "green" : "red",
                              }}
                            >
                              <span style={{ color: "#1f2937" }}>
                                {item.ltp.toFixed(2)}
                              </span>
                              <span>
                                {isUp ? "▲" : "▼"} {Math.abs(percent)}%
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td>{item.sl.toFixed(2)}</td>
                    <td>{item.target.toFixed(2)}</td>
                    <td>{item.quantity}</td>
                    <td>₹{item.allocation.toLocaleString()}</td>
                    <td>
                      {item.exit_price != null
                        ? item.exit_price.toFixed(2)
                        : "-"}
                    </td>
                    <td>
                      {item.exit_date
                        ? new Date(item.exit_date)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")
                        : "-"}
                    </td>
                    <td>
                      {item.charges != null ? item.charges.toFixed(2) : "-"}
                    </td>
                    <td>
                      {item.pnl !== null ? (
                        (() => {
                          const pnlValue = item.pnl;
                          const percent = item.allocation
                            ? ((pnlValue / item.allocation) * 100).toFixed(2)
                            : 0;
                          const isUp = pnlValue > 0;
                          const isDown = pnlValue < 0;
                          const color = isUp
                            ? "green"
                            : isDown
                              ? "red"
                              : "gray";
                          return (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                minWidth: "140px",
                                fontWeight: 600,
                                color,
                              }}
                            >
                              <span>₹{pnlValue.toLocaleString()}</span>
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {isUp ? "▲" : isDown ? "▼" : ""}
                                {Math.abs(percent)}%
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={
                          item.status === "active"
                            ? "status-active"
                            : "status-exited"
                        }
                      >
                        {item.status}
                      </span>
                    </td>
                    <td>{item.days_passed + "/" + item.max_days}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Config Popup ── */}
      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <h3>⚙️ Momentum Configuration</h3>
            <div className="popup-fields">
              <div className="popup-field">
                <label>Client ID</label>
                <input
                  type="text"
                  value={configData.clientid}
                  onChange={(e) =>
                    setConfigData({ ...configData, clientid: e.target.value })
                  }
                  placeholder="e.g. TD3762"
                />
              </div>
              <div className="popup-field">
                <label>Total Capital (₹)</label>
                <input
                  type="number"
                  value={configData.totalCapital}
                  onChange={(e) =>
                    setConfigData({
                      ...configData,
                      totalCapital: e.target.value,
                    })
                  }
                  placeholder="Enter total capital"
                />
              </div>
              <div className="popup-field">
                <label>Compounding</label>
                <select
                  value={configData.compounding}
                  onChange={(e) =>
                    setConfigData({
                      ...configData,
                      compounding: e.target.value,
                    })
                  }
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              </div>
              <div className="popup-field">
                <label>MTF Enabled</label>
                <select
                  value={configData.mtf}
                  onChange={(e) =>
                    setConfigData({ ...configData, mtf: e.target.value })
                  }
                >
                  <option value="false">False</option>
                  <option value="true">True</option>
                </select>
              </div>
              {configData.mtf === "true" && (
                <div className="popup-field">
                  <label>Leverage</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1"
                    value={configData.leverage}
                    onChange={(e) =>
                      setConfigData({ ...configData, leverage: e.target.value })
                    }
                    placeholder="e.g. 3"
                  />
                </div>
              )}
            </div>
            <div className="popup-actions">
              <button
                className="lt-btn"
                onClick={handleConfigSave}
                disabled={savingConfig}
              >
                💾 {savingConfig ? "Saving..." : "Save"}
              </button>
              <button
                className="lt-btn secondary"
                onClick={() => setShowPopup(false)}
              >
                ✖ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MomentumTrackContainer;
